/**
 * Path eligibility rule inspect view per scope §6.6.3. Atlas Day 3a P6.
 *
 * Phase 0 #11 refinement: engine-layer JOIN is fpc × marketplaces (per
 * calculate_profitability:L358-359); manufacturer_distributors lives at
 * gate-altitude judgment per scope §6.6.3 L4 not engine JOIN.
 */

import type { JSX } from 'react';
import {
  URL_SETTINGS_CHANNELS_AND_PATHS,
  URL_DIRECTORY_MANUFACTURERS,
} from '../../../lib/atlas/atlasUrls';
import { PATH_ELIGIBILITY_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PathEligibilityInspect(): JSX.Element {
  const fpcFields = FIELD_CLASSIFICATIONS.fulfillment_path_config;
  const mkFields = FIELD_CLASSIFICATIONS.marketplaces;

  return (
    <InspectShell
      subRegionName="Path eligibility"
      surfaceLine="ISG considers N path × channel combinations active. P fulfillment approaches × C marketplaces. Combinations gate which products are calculable."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.6.3: mostly mechanical at filtering layer (JOIN structure); judgment at
          gates.
        </p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <strong>Mechanical filtering layer</strong> — engine JOIN structure verified Phase
            0 #11: <code>calculate_profitability</code>:L358-359 LEFT JOIN{' '}
            <code>fulfillment_path_config fpc</code> ON{' '}
            <code>mkt.id = fpc.marketplace_id</code> + filter by{' '}
            <code>fpc.is_active</code>
          </li>
          <li>
            <strong>Judgment at gates</strong> — <code>manufacturer_distributors</code>{' '}
            pairings + <code>fpc.is_active</code> toggles + <code>marketplaces.is_active</code>{' '}
            toggles
          </li>
          <li>
            <strong>Refinement:</strong> manufacturer_distributors NOT in engine-layer JOIN per
            Phase 0 #11; lives at gate-altitude (product.manufacturer_id constraint controls
            which paths are calculable per product)
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>Split per layer per scope §6.6.3 L5:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>Mechanical: filtering JOIN structure (engine code)</li>
          <li>
            Strategic: which combos are active (<code>fpc.is_active</code> +{' '}
            <code>marketplaces.is_active</code> toggles)
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>Split per altitude per scope §6.6.3 L6:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            Structure (JOIN logic) = mechanical at engine layer; layer 6 N/A at value-altitude
          </li>
          <li>
            Active toggles (<code>fpc.is_active</code> +{' '}
            <code>marketplaces.is_active</code>) = <strong>operational</strong> per
            FIELD_CLASSIFICATIONS ({fpcFields.is_active.layer6Authority} /{' '}
            {mkFields.is_active.layer6Authority})
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>Distributed substrate per scope §6.6.3 L7:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <code>manufacturer_distributors</code> changes traced via{' '}
            <code>manufacturers.provenance</code> (Day 67 P2.2 substrate)
          </li>
          <li>
            <code>fulfillment_path_config</code> untraced per Atlas Day 2 Phase 0 #10 —{' '}
            <code>fpc.provenance</code> column absent; not in Day 82 P6.1 6-trigger cohort
          </li>
          <li>
            <code>marketplaces</code> untraced today — no provenance sidecar per Atlas Day 2.5
            schema state
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PATH_ELIGIBILITY_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.6.3 L9: split per altitude. Clean-reversible at toggle altitude (is_active
          + pairings revert cleanly with 60s cache invalidation). Structurally-irreversible at
          JOIN-structure altitude (code change to filtering).
        </p>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.6.3 L11. Surfaces in title block sentence 2
          channel enumeration (Atlas Day 1 D4 lock: 4 marketplaces alphabetical;
          path-altitude detail surfaces here in Path eligibility inspect).
        </p>
      </LayerSection>

      <LayerSection layer={4 /* cross-links footer */} title="Cross-links">
        <ul className="list-disc pl-5 text-xs">
          <li>
            <a href={URL_SETTINGS_CHANNELS_AND_PATHS} className="text-accent hover:underline">
              Settings → Channels &amp; Paths
            </a>{' '}
            (per-path + per-marketplace is_active toggle + metadata)
          </li>
          <li>
            <a href={URL_DIRECTORY_MANUFACTURERS} className="text-accent hover:underline">
              Directory → Manufacturers
            </a>{' '}
            (manufacturer_distributors pairings)
          </li>
        </ul>
      </LayerSection>
    </InspectShell>
  );
}
